/**
 * Comprehensive formatting utilities for the Edges design system
 *
 * This module provides a unified formatting system with support for:
 * - Text formatting (case conversion, truncation, etc.)
 * - Number formatting (decimals, thousands separators, compact notation)
 * - Currency formatting (multiple currencies and display options)
 * - Date formatting (relative time, custom formats, timezones)
 * - Boolean formatting (custom true/false text)
 * - Energy units (Wh, kWh, MWh, GWh with auto-scaling)
 * - Temperature units (Celsius, Fahrenheit, Kelvin)
 * - Distance units (meters, kilometers, miles, feet)
 * - Phone number formatting (US and international)
 * - Custom formatters via the FormatRegistry
 */

export * from "./boolean";
export * from "./capacity";
export * from "./component-formatter";
export * from "./csvUnit";
export * from "./current";
export * from "./date";
export * from "./distance";
export * from "./energy";
// `formatFieldValue` lives in its own leaf module so downstream helpers (see
// `./csvUnit.ts`) can depend on the dispatcher without transitively pulling in
// the whole formatting barrel. Re-exported here to keep the public API stable.
export { formatFieldValue } from "./formatFieldValue";
export * from "./number";
export * from "./phone";
export * from "./power";
// Export the registry
export { FormatRegistry } from "./registry";
export * from "./resistance";
export * from "./temperature";
// Export all formatting utilities
export * from "./text";
// Export all types
export * from "./types";
export * from "./voltage";

// Import types for `createFormat` below.
import type { FieldFormat } from "./types";

/**
 * Convenience function to create a format object
 *
 * @example
 * ```typescript
 * const currencyFormat = createFormat("currency", { currency: "EUR" });
 * const dateFormat = createFormat("date", { relative: true });
 * ```
 */
export const createFormat = <T extends FieldFormat["type"]>(
  type: T,
  options?: Omit<Extract<FieldFormat, { type: T }>, "type">
): Extract<FieldFormat, { type: T }> => {
  return { type, ...options } as Extract<FieldFormat, { type: T }>;
};
